import { DropOverMenu } from "client/Components";
import { EmbedInputDialogData } from "client/Features/Hub/HubMediaSelect/EmbedInputDialog";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState,
	KeyboardEvent,
	forwardRef,
	Ref,
	useImperativeHandle
} from "react";
import { ElementPicker } from "./ElementPicker";
import styles from "./AddElementMenu.module.less";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { VideoInputDialogData } from "client/Features/Hub/HubMediaSelect/VideoInputDialog";
import { HubItemElementFormModel } from "client/Features/Hub/Management/common";
import {
	KeyboardGridNavigationContext,
	KeyboardGridNavigationHandlers,
	useKeyboardGridNavigation
} from "client/Hooks/useKeyboardGridNavigation";
import { useFocusTrap } from "client/Hooks/useFocusTrap";
import { Key } from "client/Misc/commonTypes";
import { ImageWithAttribution, HubImageSelectorKind } from "client/Features/Hub/Management/useHubImageSelector";

export type AddElementMenuHandle = {
	focusFirstElement(): void;
};

export type AddElementMenuProps = {
	target: HTMLElement;
	onNewElement(element: HubItemElementFormModel): void;
	onClose(): void;
	onOpenEmbedInputDialog(
		data: EmbedInputDialogData
	): Promise<TryResult<string>>;
	onOpenVideoInputDialog(
		data: VideoInputDialogData
	): Promise<TryResult<string>>;
	trackingId: string;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
};

export const AddElementMenu = forwardRef(
	(props: AddElementMenuProps, ref: Ref<AddElementMenuHandle>): ReactElement => {
		const {
			target,
			onClose,
			onNewElement,
			onOpenEmbedInputDialog,
			onOpenVideoInputDialog,
			onSelectImage,
			trackingId
		} = props;

		const [lastFocusableIndex, setLastFocusableIndex] = useState<number>(0);

		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
		useFocusTrap<HTMLDivElement>(containerRef);

		const elementPickerNavigation: KeyboardGridNavigationHandlers = useKeyboardGridNavigation({
			onFocusChange: (index: number): void => {
				setLastFocusableIndex(index);
			}
		});

		useImperativeHandle(
			ref,
			(): AddElementMenuHandle => ({
				focusFirstElement(): void {
					setLastFocusableIndex(0);
					elementPickerNavigation.focusItemAtHandler("start");
				}
			}),
			[elementPickerNavigation]
		);

		const menuItemsFocusResetHandler = useCallback(
			(): void => {
				// We need to use setTimeout to ensure that the focus is reset after the new items are rendered
				setTimeout(
					() => {
						setLastFocusableIndex(0);
						elementPickerNavigation.focusItemAtHandler("start");
					},
					0
				);
			},
			[elementPickerNavigation]
		);

		const keyUpHandler = useCallback(
			(event: KeyboardEvent): void => {
				if (event.key === Key.Escape) {
					onClose();
				}
			},
			[onClose]
		);

		return (
			<DropOverMenu
				target={target}
				onClose={onClose}
				title={localize("_Add")}
				trackingId={generateTrackingId(trackingId, "__wrapper")}
				onKeyUp={keyUpHandler}
			>
				<div
					{...setTrackingId(trackingId)}
					className={styles.container}
					ref={containerRef}
				>
					<KeyboardGridNavigationContext.Provider value={elementPickerNavigation.contextData}>
						<ElementPicker
							onMenuItemsFocusReset={menuItemsFocusResetHandler}
							tabbableIndex={lastFocusableIndex}
							onSelect={onNewElement}
							onOpenEmbedInputDialog={onOpenEmbedInputDialog}
							onOpenVideoInputDialog={onOpenVideoInputDialog}
							onSelectImage={onSelectImage}
						/>
					</KeyboardGridNavigationContext.Provider>
				</div>
			</DropOverMenu>
		);
	}
);
AddElementMenu.displayName = "AddElementMenu";